// ABOUTME: The "nothing more will be spoken" teardown of the agent's queued Web Audio playback.
// ABOUTME: Extracted from App.js's stop_audio handler so the manual STOP button shares its semantics.

/**
 * Kill whatever the agent has already scheduled into the Web Audio graph.
 *
 * App.js schedules every inbound PCM chunk ahead of real time (`source.start(nextStartTime)`), so by
 * the time a guest decides they have heard enough there can be several seconds of speech sitting in
 * the graph with no socket left to stop it — closing the socket, muting the mic or telling the
 * backend to stop all do nothing to audio that is already queued locally. Only stopping the sources
 * does. That is why this exists as its own step rather than as part of "tell the backend to stop".
 *
 * This is a faithful extraction of the `control` / `stop_audio` handler's flush in App.js (grep
 * `stop_audio` — line numbers here have already drifted once), which is the correct template of
 * the two copies there: it is the one that means "the rest of this answer will never be heard",
 * which is exactly what the manual STOP button means too. The VAD barge-in copy — the `onInterrupt`
 * callback App.js passes to `useVoiceActivityDetection` — is deliberately NOT touched: that is
 * locked audio code and refactoring it alongside a new feature is how regressions get shipped.
 * Folding both call sites onto this helper is a separate, single-purpose change.
 *
 * Four things happen, and the order matters only for the last one:
 *  1. every live source is stopped — the audible part;
 *  2. the bookkeeping array is emptied, because `source.onended` will not necessarily run for a
 *     source stopped this way and a stale entry would be stopped again on the next flush;
 *  3. `nextStartTime` is rewound to the context's clock, or the NEXT utterance would be scheduled
 *     into the future slot the discarded speech had already reserved and start seconds late;
 *  4. the speech clock is reset, so the caption stops pacing itself against audio nobody will hear
 *     (`speechClock.js`: with the queue gone, progress would otherwise read as stalled).
 *
 * Everything is optional and everything is defended. A source whose `.stop()` throws (Web Audio
 * throws `InvalidStateError` for a node that was never started) must not prevent the remaining
 * sources from being stopped — one bad node cannot be allowed to leave the agent talking over a
 * guest who asked for silence.
 *
 * React state is deliberately NOT touched here. The flags (`isAgentSpeaking`, `agentState`, ...) are
 * the caller's concern; keeping them out is what makes this callable and assertable without React.
 *
 * @param {object}  p
 * @param {{current: (AudioContext|null)}} [p.audioContext]           ref holding the playback context
 * @param {{current: Array}}               [p.activeAudioSourcesRef]  ref holding the live sources
 * @param {{current: number}}              [p.nextStartTime]          ref holding the scheduling cursor
 * @param {{current: *}}                   [p.speakingTimeoutRef]     ref holding the end-of-speech timer
 * @param {Function}                       [p.resetSpeechClock]       `speechClock.resetSpeechClock`
 * @returns {number} how many sources were stopped WITHOUT throwing. A source that throws is already
 *                   finished or was never started, so it is not counted — the number is "how much
 *                   speech did this actually silence", which is what a test wants to assert on.
 */
export function flushAgentAudio({
  audioContext,
  activeAudioSourcesRef,
  nextStartTime,
  speakingTimeoutRef,
  resetSpeechClock,
} = {}) {
  const sources = Array.isArray(activeAudioSourcesRef?.current) ? activeAudioSourcesRef.current : [];

  let stopped = 0;
  for (const source of sources) {
    try {
      source.stop();
      stopped += 1;
    } catch (e) {
      // Already ended, or never started. Nothing to silence, nothing to report.
    }
  }

  if (activeAudioSourcesRef) activeAudioSourcesRef.current = [];

  if (nextStartTime && typeof audioContext?.current?.currentTime === 'number') {
    nextStartTime.current = audioContext.current.currentTime;
  }

  if (typeof resetSpeechClock === 'function') resetSpeechClock();

  if (speakingTimeoutRef?.current) {
    clearTimeout(speakingTimeoutRef.current);
    // Nulled as well as cleared, which App.js's inline copy does not do. Every reader of this ref
    // tests it for truthiness before clearing; leaving a spent id behind means the next flush
    // clears a number the runtime may since have handed to somebody else's timer.
    speakingTimeoutRef.current = null;
  }

  return stopped;
}

export default flushAgentAudio;
